import './styles.css';
import { createGame, startHand, getLegalActions, act, toggleDiscard, confirmDraw, PHASE, assertIntegrity, formatMoney } from './game/game.js';
import { chooseAction, PERSONALITIES } from './game/ai.js';
import { cardParts, cardLabel } from './game/cards.js';
import { evaluateHand } from './game/evaluator.js';
import { DENOMINATIONS, DEFAULT_COIN_ORDER, TABLE_MODES, coinPieces, decomposeCurrency, normalizeCoinOrder, preferCoin } from './game/currency.js';
import { actionCue, atmosphereFor } from './game/presentation.js';
import { AudioDirector } from './audio.js';

const app = document.querySelector('#app');
const audio = new AudioDirector();
let state = createGame();
let aiTimer = null;
let introOpen = true;
let settingsOpen = false;
let helpOpen = false;
let currencyOpen = false;
let reducedMotion = localStorage.getItem('poker-reduced-motion') === 'true';
let largeCards = localStorage.getItem('poker-large-cards') === 'true';
let coinOrder = normalizeCoinOrder(JSON.parse(localStorage.getItem('poker-coin-order-v1') || 'null') || DEFAULT_COIN_ORDER);

const mediaRoot = new URL(`${import.meta.env.BASE_URL}media/`, document.baseURI);
const media = path => `url("${new URL(path, mediaRoot).href}")`;
const mediaVariables = {
  '--media-saloon': media('saloon-pov.webp'),
  '--media-materials': media('material-atlas.webp'),
  '--media-props': media('props-atlas.webp'),
  '--media-stakes-effects': media('stakes-effects-atlas-v1.webp'),
  '--media-robbery': media('robbery-atlas-v1.webp'),
  ...Object.fromEntries(['mara', 'mateo', 'isaiah', 'mei', 'finn', 'eli'].map(id => [`--media-patron-${id}`, media(`patrons/${id}.webp`)])),
  ...Object.fromEntries(['indian-cent', 'seated-dime', 'seated-quarter', 'seated-half', 'morgan-dollar', 'double-eagle'].map(id => [`--media-coin-${id}`, media(`coins/${id}.webp`)]))
};
Object.entries(mediaVariables).forEach(([name, value]) => document.documentElement.style.setProperty(name, value));

const escapeHtml = value => String(value).replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));

function wagerPieces(value, limit = 10, order = DEFAULT_COIN_ORDER) {
  const pieces = coinPieces(value, order, limit);
  return pieces.map((piece, index) => {
    const tilt = ((index * 7 + piece.value) % 13) - 6;
    const stagger = ((index * 11 + piece.value) % 9) - 4;
    const view = (index * 5 + Math.round(value / Math.max(1, piece.value))) % 8;
    return `<i class="wager-piece ${piece.id} coin-view-${view}" style="--i:${index};--tilt:${tilt}deg;--stagger:${stagger}px" title="${piece.name}; ${formatMoney(piece.value)}"></i>`;
  }).join('');
}

function cardMarkup(card, index, { hidden = false, selectable = false, selected = false, small = false, fan = null } = {}) {
  const fanStyle = fan ? ` style="--fan-angle:${fan.angle}deg;--fan-y:${fan.y}px;--fan-x:${fan.x}px"` : '';
  if (hidden) return `<div class="playing-card card-back ${small ? 'small' : ''}"${fanStyle} role="img" aria-label="Face-down card"><span class="back-medallion">EG</span></div>`;
  const info = cardParts(card);
  const label = cardLabel(card);
  return `<button class="playing-card card-face ${info.color} ${small ? 'small' : ''} ${selected ? 'selected' : ''}"${fanStyle} ${selectable ? `data-card-index="${index}"` : 'disabled'} aria-label="${escapeHtml(label)}${selected ? ', selected to discard' : ''}" aria-pressed="${selected}">
    <span class="corner top"><b>${info.rank}</b><i>${info.symbol}</i></span>
    <span class="pip">${info.symbol}</span>
    <span class="corner bottom"><b>${info.rank}</b><i>${info.symbol}</i></span>
    ${selected ? '<span class="discard-mark">DISCARD</span>' : ''}
  </button>`;
}

function opponentMarkup(seat, position) {
  const show = state.phase === PHASE.SHOWDOWN && !seat.folded;
  const isWinner = state.result?.winners?.includes(seat.id);
  const isActing = state.actingSeat === seat.id;
  const profile = PERSONALITIES[seat.aiIndex];
  const emotion = state.result
    ? (isWinner ? (seat.stack % 2 ? 'happy' : 'pleased') : (seat.stack < 1000 ? 'angry' : 'sad'))
    : isActing ? 'thinking'
      : /RAISES|BETS/.test(seat.lastAction) ? 'anticipation'
        : /FOLDS/.test(seat.lastAction) ? 'sad'
          : /TAKES A SEAT/.test(seat.lastAction) ? 'pleased' : 'neutral';
  const cards = seat.cards.length ? seat.cards.map((card, index) => {
    const seed = state.handNumber * 37 + seat.id * 19 + index * 11 + card.charCodeAt(0) * 3 + card.charCodeAt(1);
    const fan = { angle: ((seed % 5) - 2) * 0.55, y: (seed * 7) % 5 - 2, x: (seed * 11) % 3 - 1 };
    return cardMarkup(card, index, { hidden: !show, small: true, fan });
  }).join('') : '';
  return `<section class="opponent opponent-${position} ${seat.folded ? 'folded' : ''} ${isActing ? 'acting' : ''} ${isWinner ? 'winner' : ''}" aria-label="${profile.name}, ${profile.title}">
    <div class="character patron-${profile.id} emotion-${emotion}" aria-hidden="true"></div>
    <div class="opponent-action" aria-live="polite">${escapeHtml(seat.lastAction || (isActing ? 'THINKING…' : ''))}</div>
    <div class="opponent-cards">${cards}</div>
    <div class="nameplate">
      <div><strong>${profile.name}</strong><span>${profile.title}</span></div>
      <div class="seat-bank"><div class="wager-stack tiny">${wagerPieces(seat.stack, 8)}</div><b>${formatMoney(seat.stack)}</b></div>
    </div>
    ${state.dealer === seat.id ? '<div class="dealer-marker" title="Dealer">D</div>' : ''}
  </section>`;
}

function currencyTrayMarkup(player) {
  const counts = decomposeCurrency(player.stack, coinOrder);
  const preferred = coinOrder[0];
  const placed = player.roundBet ? wagerPieces(player.roundBet, 8, coinOrder) : '';
  return `<aside class="currency-tray ${currencyOpen ? 'open' : ''}" aria-label="Your period-currency purse">
    <button type="button" class="currency-toggle" data-action="toggle-purse" aria-expanded="${currencyOpen}"><div><small>YOUR PURSE</small><strong>${formatMoney(player.stack)}</strong></div><span>${TABLE_MODES[state.tableMode].label}</span></button>
    <div class="coin-sorter" role="list" aria-label="Tap a denomination to sort it first">
      ${DENOMINATIONS.map(coin => `<button type="button" role="listitem" class="currency-choice ${coin.id === preferred ? 'preferred' : ''}" data-coin-id="${coin.id}" aria-pressed="${coin.id === preferred}" title="Prefer ${coin.name}">
        <i class="tray-coin ${coin.id} coin-view-0" aria-hidden="true"></i><span>${coin.short}</span><b>×${counts[coin.id] || 0}</b>
      </button>`).join('')}
    </div>
    <p>${player.roundBet ? `Placed this round: ${formatMoney(player.roundBet)}` : `Preferred wager coin: ${DENOMINATIONS.find(coin => coin.id === preferred).short}`}</p>
    ${placed ? `<div class="tray-wager wager-stack">${placed}</div>` : ''}
  </aside>`;
}

function actionMarkup() {
  if (state.phase === PHASE.IDLE) return '<button class="primary-action" data-action="start">Take a seat</button>';
  if (state.phase === PHASE.DRAW && !state.seats[0].folded) {
    const count = state.selectedDiscards.length;
    return `<button class="primary-action" data-action="draw">${count ? `Draw ${count}` : 'Stand pat'}</button><span class="action-hint">Tap up to 3 cards</span>`;
  }
  if (state.phase === PHASE.SHOWDOWN) return `<button class="primary-action" data-action="next-hand">Deal next hand</button><button class="secondary-action" data-action="history">Read the table</button>`;
  if (state.phase === PHASE.GAME_OVER) return '<button class="primary-action" data-action="restart">Buy in again</button>';
  if (state.actingSeat === 0) {
    return getLegalActions(state, 0).map(action => `<button class="${action.type === 'FOLD' ? 'danger-action' : action.type === 'RAISE' || action.type === 'BET' ? 'primary-action' : 'secondary-action'}" data-poker-action="${action.type}">${action.label}</button>`).join('');
  }
  return '<span class="waiting"><i></i> The table is thinking</span>';
}

function resultMarkup() {
  if (!state.result) return '';
  if (state.robbery) return `<div class="result-banner robbery-banner" role="alert">
    <small>HOLDUP AT EMBER GULCH</small><strong>The saloon is robbed!</strong><span>Masked outlaws take ${formatMoney(state.robbery.amount)}. ${escapeHtml(state.result.title)}—and this robbery cannot happen again.</span>
  </div>`;
  return `<div class="result-banner ${state.result.winners.includes(0) ? 'player-won' : ''}" role="status">
    <small>SHOWDOWN</small><strong>${escapeHtml(state.result.title)}</strong><span>${escapeHtml(state.result.detail)}</span>
  </div>`;
}

function robberyMarkup() {
  if (!state.robbery) return '';
  return `<div class="robbery-scene" aria-hidden="true">
    <i class="robbery-stage robbery-stage-1"></i><i class="robbery-stage robbery-stage-2"></i><i class="robbery-stage robbery-stage-3"></i><i class="robbery-stage robbery-stage-4"></i>
  </div>`;
}

function modalMarkup() {
  if (introOpen) return `<div class="modal-scrim"><section class="intro-card" data-modal-panel role="dialog" aria-modal="true" aria-labelledby="game-title">
    <span class="eyebrow">INCOPREA PRESENTS</span>
    <h1 id="game-title"><span>EMBER GULCH SALOON</span> POKER</h1>
    <p class="intro-copy">Night has settled over Ember Gulch. Three strangers have kept a chair open—and none of them came to lose.</p>
    <div class="rule-row"><span>FIVE-CARD DRAW</span><span>ADAPTIVE TABLE</span><span>PERIOD COIN STAKES</span></div>
    <div class="table-mode-picker" role="radiogroup" aria-label="Choose table stakes">
      ${Object.values(TABLE_MODES).map(mode => `<button type="button" role="radio" aria-checked="${state.tableMode === mode.id}" class="${state.tableMode === mode.id ? 'selected' : ''}" data-table-mode="${mode.id}"><b>${mode.label}</b><span>${formatMoney(mode.buyIn)} purse · ${formatMoney(mode.ante)} ante</span></button>`).join('')}
    </div>
    <button class="primary-action enter-saloon" data-action="enter">Enter the saloon</button>
    <small>Skill and chance. No real-money gambling.</small>
  </section></div>`;
  if (settingsOpen) return `<div class="modal-scrim" data-action="close-modal"><section class="settings-card" data-modal-panel role="dialog" aria-modal="true" aria-label="Settings">
    <header><h2>Table settings</h2><button class="icon-button" data-action="close-modal" aria-label="Close settings">×</button></header>
    <label><span><b>Music</b><small>Original saloon score</small></span><input type="checkbox" data-setting="music" ${audio.musicEnabled ? 'checked' : ''}></label>
    <label><span><b>Table sounds</b><small>Cards, chips and wins</small></span><input type="checkbox" data-setting="sfx" ${audio.sfxEnabled ? 'checked' : ''}></label>
    <label><span><b>Reduced motion</b><small>Quicker deals and no drifting smoke</small></span><input type="checkbox" data-setting="motion" ${reducedMotion ? 'checked' : ''}></label>
    <label><span><b>Large cards</b><small>Increase hand size</small></span><input type="checkbox" data-setting="cards" ${largeCards ? 'checked' : ''}></label>
  </section></div>`;
  if (helpOpen) return `<div class="modal-scrim" data-action="close-modal"><section class="help-card" data-modal-panel role="dialog" aria-modal="true" aria-label="How to play">
    <header><h2>Five-Card Draw</h2><button class="icon-button" data-action="close-modal" aria-label="Close help">×</button></header>
    <ol><li>Every player antes ${formatMoney(state.ante)} and receives five hidden cards.</li><li>Bet, check, call, raise or fold before the draw.</li><li>Tap up to three cards, then draw replacements—or stand pat.</li><li>A final betting round leads to showdown. The best five-card hand wins.</li></ol>
    <p>Hand order: high card, pair, two pair, three of a kind, straight, flush, full house, four of a kind, straight flush.</p>
  </section></div>`;
  return '';
}

function render() {
  const player = state.seats[0];
  const atmosphere = atmosphereFor(state);
  const visibleOpponents = state.seats.slice(1).filter(seat => !seat.sittingOut);
  const positions = visibleOpponents.length === 1 ? ['center'] : ['left', 'center', 'right'];
  const selectable = state.phase === PHASE.DRAW && !player.folded;
  const playerCards = player.cards.map((card, index) => {
    const seed = state.handNumber * 41 + index * 13 + card.charCodeAt(0) * 5 + card.charCodeAt(1);
    const fan = { angle: ((seed % 7) - 3) * 0.32, y: (seed * 5) % 5 - 2, x: (seed * 3) % 3 - 1 };
    return cardMarkup(card, index, { selectable, selected: state.selectedDiscards.includes(index), fan });
  }).join('');
  const handName = player.cards.length === 5 ? evaluateHand(player.cards).name : 'Waiting for the deal';
  app.innerHTML = `<main class="game ${atmosphere.id} ${introOpen ? 'intro-open' : ''} ${reducedMotion ? 'reduce-motion' : ''} ${largeCards ? 'large-cards' : ''}">
    <div class="saloon-layer" aria-hidden="true"></div>
    <div class="light-layer" aria-hidden="true"></div>
    <div class="dust-layer" aria-hidden="true"></div>
    <div class="vignette" aria-hidden="true"></div>
    <header class="topbar">
      <div class="brand-lockup"><span class="ember-mark">♠</span><div><b>EMBER GULCH</b><small>FIVE-CARD DRAW</small></div></div>
    <div class="table-stats"><span><small>POT</small><b>${formatMoney(state.pot)}</b></span><span><small>HAND</small><b>${state.handNumber || '—'}</b></span></div>
      <nav><button class="icon-button" data-action="help" aria-label="How to play">?</button><button class="icon-button" data-action="settings" aria-label="Settings">⚙</button></nav>
    </header>

    <div class="table-message" aria-live="polite">${escapeHtml(state.message)}</div>
    ${visibleOpponents.map((seat, index) => opponentMarkup(seat, positions[index])).join('')}

    <div class="table-props" aria-hidden="true"><i class="prop whiskey"></i><i class="prop smoke"></i></div>
    <div class="stakes-effects" aria-hidden="true"><i class="stakes-prop stakes-cigar"></i><i class="stakes-prop stakes-bottle"></i><i class="stakes-prop stakes-drink"></i><i class="stakes-prop stakes-lamp"></i><i class="stakes-prop stakes-crowd-left"></i><i class="stakes-prop stakes-crowd-right"></i><i class="stakes-prop stakes-glow"></i><i class="stakes-prop stakes-treasure"></i></div>
    <div class="pot-visual" aria-label="Pot: ${formatMoney(state.pot)}"><div class="wager-stack pot-stack">${wagerPieces(state.pot, 12)}</div><span>${state.pot ? `${formatMoney(state.pot)} IN THE POT` : `${formatMoney(state.ante)} ANTE`}</span></div>
    ${robberyMarkup()}
    ${resultMarkup()}

    <section class="player-area ${state.actingSeat === 0 ? 'your-turn' : ''}" aria-label="Your hand">
      <div class="hand-meta"><span>${handName}</span><div class="player-bank"><div class="wager-stack">${wagerPieces(player.stack, 10, coinOrder)}</div><b>${formatMoney(player.stack)}</b></div></div>
      <div class="player-cards">${playerCards}</div>
      ${state.dealer === 0 ? '<div class="dealer-marker player-dealer" title="You are dealer">D</div>' : ''}
    </section>
    ${currencyTrayMarkup(player)}
    <footer class="action-dock" aria-label="Poker actions">${actionMarkup()}</footer>
    <div class="legal">Fictional play • Period coin renditions • No cash value</div>
    ${state.turnover?.length ? `<div class="turnover-toast">${state.turnover.map(item => `${escapeHtml(item.from)} ${item.angry ? 'storms away' : 'cashes out'}; ${escapeHtml(item.to)} takes the chair.`).join(' ')}</div>` : ''}
    ${modalMarkup()}
    <div class="sr-only" aria-live="polite">${escapeHtml(state.message)}</div>
  </main>`;
}

function commit(next, cue) {
  const previousPhase = state.phase;
  const robberyJustHappened = Boolean(next.robbery && !state.robbery);
  state = next;
  assertIntegrity(state);
  audio.setMood(atmosphereFor(state).music);
  if (robberyJustHappened) audio.cue('robbery');
  else if (state.turnover?.length) {
    audio.cue('leave');
    setTimeout(() => audio.cue('deal'), 500);
  } else if (state.phase === PHASE.SHOWDOWN && previousPhase !== PHASE.SHOWDOWN && cue !== 'fold') audio.cue('win');
  else if (cue) audio.cue(cue);
  render();
  scheduleAI();
}

function scheduleAI() {
  clearTimeout(aiTimer);
  if (![PHASE.BET_PRE, PHASE.BET_POST].includes(state.phase) || state.actingSeat === null || state.actingSeat === 0) return;
  const revision = state.revision;
  const delay = reducedMotion ? 80 : 520 + Math.floor(Math.random() * 520);
  aiTimer = setTimeout(() => {
    if (state.revision !== revision || state.actingSeat === 0) return;
    const seat = state.seats[state.actingSeat];
    const action = chooseAction({ seat, legal: getLegalActions(state), phase: state.phase });
    commit(act(state, seat.id, action), actionCue(action.type));
  }, delay);
}

app.addEventListener('click', async event => {
  const card = event.target.closest('[data-card-index]');
  if (card) {
    commit(toggleDiscard(state, Number(card.dataset.cardIndex)), 'card');
    return;
  }
  const poker = event.target.closest('[data-poker-action]');
  if (poker) {
    if (state.actingSeat !== 0) return;
    const action = getLegalActions(state, 0).find(item => item.type === poker.dataset.pokerAction);
    if (action) commit(act(state, 0, action), actionCue(action.type));
    return;
  }
  const coinChoice = event.target.closest('[data-coin-id]');
  if (coinChoice) {
    coinOrder = preferCoin(coinOrder, coinChoice.dataset.coinId);
    localStorage.setItem('poker-coin-order-v1', JSON.stringify(coinOrder));
    audio.cue('chip');
    render();
    return;
  }
  const tableMode = event.target.closest('[data-table-mode]');
  if (tableMode && introOpen) {
    state = createGame(undefined, Math.random, tableMode.dataset.tableMode);
    render();
    return;
  }
  const actionTarget = event.target.closest('[data-action]');
  if (!actionTarget) return;
  if (actionTarget.classList.contains('modal-scrim') && event.target !== actionTarget) return;
  const action = actionTarget.dataset.action;
  if (action === 'enter') {
    const unlockPromise = audio.unlock();
    introOpen = false;
    commit(startHand(state));
    void unlockPromise.then(() => audio.cue('deal'));
  } else if (action === 'start' || action === 'next-hand') {
    void audio.unlock();
    commit(startHand(state), 'deal');
  } else if (action === 'restart') {
    state = createGame(undefined, Math.random, state.tableMode);
    commit(startHand(state), 'deal');
  } else if (action === 'draw') commit(confirmDraw(state), 'draw');
  else if (action === 'toggle-purse') { currencyOpen = !currencyOpen; render(); }
  else if (action === 'settings') { settingsOpen = true; render(); }
  else if (action === 'help' || action === 'history') { helpOpen = true; render(); }
  else if (action === 'close-modal') { settingsOpen = false; helpOpen = false; render(); }
});

app.addEventListener('change', event => {
  const setting = event.target.dataset.setting;
  if (!setting) return;
  if (setting === 'music') audio.setMusic(event.target.checked);
  if (setting === 'sfx') audio.setSfx(event.target.checked);
  if (setting === 'motion') {
    reducedMotion = event.target.checked;
    localStorage.setItem('poker-reduced-motion', reducedMotion);
  }
  if (setting === 'cards') {
    largeCards = event.target.checked;
    localStorage.setItem('poker-large-cards', largeCards);
  }
  render();
});

document.addEventListener('keydown', event => {
  if (event.key === 'Escape' && (settingsOpen || helpOpen)) {
    settingsOpen = false;
    helpOpen = false;
    render();
  }
});

if ('serviceWorker' in navigator && import.meta.env.PROD) navigator.serviceWorker.register('./sw.js').catch(() => {});
render();
